import React, { useState, useEffect } from 'react';
import {
  Button,
  Box,
  Typography,
  CircularProgress,
  TextField,
  Tooltip,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Edit,
  Delete,
  Add,
  Search,
  CheckCircle,
  Cancel,
  Visibility,
  PlayArrow
} from '@mui/icons-material';
import AgentNode from '../models/Nodes/AgentNode';
import SemanticMemory from '../models/Memories/SemanticMemory';
import ProceduralMemory from '../models/Memories/ProceduralMemory';
import AgentEditWizardModal from '../components/Modals/AgentEditWizardModal';
import AgentCreateWizardModal from '../components/Modals/AgentCreateWizardModal';
import AgentStatusModal from '../components/Modals/AgentStatusModal';
import AgentViewModal from '../components/Modals/AgentViewModal';
import ExecuteWorkflowModal from '../components/Modals/ExecuteWorkflowModal';
import { useEnvironment } from '../context/EnvironmentContext';
import WorkflowService from '../services/WorkflowService';
import { useApiToken } from '../hooks/useApiToken';

const AgentsDashboardPage = () => {
  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showExecuteModal, setShowExecuteModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<AgentNode | null>(null);
  const [agents, setAgents] = useState<AgentNode[]>([]);
  const [searchText, setSearchText] = useState('');
  const { environment } = useEnvironment();
  const [workflowInputs, setWorkflowInputs] = useState<Record<string, any>>({});
  const [edges, setEdges] = useState<any[]>([]);
  const getAccessToken = useApiToken();

  useEffect(() => {
    fetchAgents();
  }, [environment]);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const accessToken = await getAccessToken();
      const workflowService = new WorkflowService(environment, accessToken);
      const response = await workflowService.listAgents();
      const agentsData = Array.isArray(response.data?.agents) ? response.data.agents : [];

      const parsedAgents = agentsData.map(agentData => {
        const agent = new AgentNode(
          agentData._id,
          SemanticMemory.fromJson(agentData.semantic_memory_args),
          ProceduralMemory.fromJson(agentData.procedural_memory_args),
          agentData.type || 'agent_template',
          null,
          null,
          [],
          agentData.parent_agent_id,
          agentData.required_parameters || [],
          agentData.agent_dependencies || [],
          agentData.agent_template_id
        );
        agent.isEnabled = agentData.is_enabled ?? true;
        return agent;
      });

      setAgents(parsedAgents);
    } catch (error) {
      // TODO: Replace with MUI notification system
      console.error('Failed to fetch agents');
      console.error('Error fetching agents:', error);
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  const getEdges = () => edges;

  const handleEdit = (agent: AgentNode, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedAgent(agent);
    setShowEditModal(true);
  };

  const handleView = (agent: AgentNode) => {
    setSelectedAgent(agent);
    setShowViewModal(true);
  };

  const handleExecute = (agent: AgentNode, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedAgent(agent);
    setShowExecuteModal(true);
  };

  const handleDelete = (agent: AgentNode, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedAgent(agent);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!selectedAgent) return;

    try {
      const accessToken = await getAccessToken();
      const workflowService = new WorkflowService(environment, accessToken);
      await workflowService.deleteAgent(selectedAgent.id);
      await fetchAgents();
      // TODO: Replace with MUI notification system
      console.log('Agent deleted successfully');
    } catch (error) {
      // TODO: Replace with MUI notification system
      console.error('Failed to delete agent');
      console.error('Error deleting agent:', error);
    } finally {
      setShowDeleteDialog(false);
      setSelectedAgent(null);
    }
  };

  const handleAddNew = () => {
    setShowCreateModal(true);
  };

  const handleAgentUpdate = async (updatedAgent: AgentNode) => {
    try {
      const accessToken = await getAccessToken();
      const workflowService = new WorkflowService(environment, accessToken);
      const agentData = updatedAgent.toJson();

      // Ensure _id is properly set for the API
      if (!agentData._id || agentData._id.trim() === '') {
        throw new Error('Agent ID is required but missing');
      }

      // Ensure type is set correctly
      agentData.type = agentData.type || 'agent_template';

      if (selectedAgent) {
        await workflowService.updateAgent(selectedAgent.id, agentData);
      } else {
        await workflowService.createAgent(agentData);
      }
      await fetchAgents();
      setShowEditModal(false);
      setShowCreateModal(false);
      // TODO: Replace with MUI notification system
      console.log(`Agent ${selectedAgent ? 'updated' : 'created'} successfully`);
    } catch (error) {
      console.error('Agent save error details:', error);
      // TODO: Replace with MUI notification system
      console.error(`Failed to ${selectedAgent ? 'update' : 'create'} agent`);
      console.error('Error saving agent:', error, 'Agent data:', updatedAgent.toJson());
    }
  };

  const handleToggleStatus = (agent: AgentNode, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedAgent(agent);
    setShowStatusModal(true);
  };

  const handleStatusConfirm = async () => {
    if (!selectedAgent) return;

    try {
      const accessToken = await getAccessToken();
      const workflowService = new WorkflowService(environment, accessToken);
      await workflowService.toggleAgentStatus(
        selectedAgent.id,
        !selectedAgent.isEnabled
      );
      await fetchAgents();
      // TODO: Replace with MUI notification system
      console.log(`Agent ${!selectedAgent.isEnabled ? 'enabled' : 'disabled'} successfully`);
    } catch (error) {
      // TODO: Replace with MUI notification system
      console.error('Failed to update agent status');
      console.error('Error updating agent status:', error);
    } finally {
      setShowStatusModal(false);
      setSelectedAgent(null);
    }
  };

  const filteredAgents = agents.filter(agent =>
    agent.id.toLowerCase().includes(searchText.toLowerCase())
  );

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'input_agent':
        return 'info';
      case 'agent_template':
        return 'secondary';
      default:
        return 'primary';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'input_agent':
        return 'Input';
      case 'agent_template':
        return 'Standard';
      default:
        return 'Standard';
    }
  };

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" sx={{ height: '100vh' }}>
        <CircularProgress size="large" />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4">Agents</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddNew}
          size="small"
        >
          Create New
        </Button>
      </Box>

      <Box sx={{ mb: 2 }}>
        <TextField
          placeholder="Search agents by ID"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ color: 'grey.400', mr: 1 }} />
          }}
          sx={{ maxWidth: 400 }}
          size="small"
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">Steps</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Model</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAgents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="textSecondary" sx={{ mb: 2 }}>No agents found</Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleAddNew}
                    size="small"
                  >
                    Create New
                  </Button>
                </TableCell>
              </TableRow>
            ) : (
              filteredAgents.map((agent) => (
                <TableRow
                  key={agent.id}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'grey.50' },
                    bgcolor: agent.parentAgentId ? 'blue.50' : 'transparent'
                  }}
                  onClick={() => handleView(agent)}
                >
                  <TableCell>
                    <Box sx={{ color: agent.parentAgentId ? 'blue.600' : 'inherit' }}>
                      {agent.id}
                      {agent.parentAgentId && (
                        <Typography variant="caption" color="textSecondary" display="block">
                          Parent: {agent.parentAgentId}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getTypeLabel(agent.type)}
                      color={getTypeColor(agent.type) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      onClick={(e) => handleToggleStatus(agent, e)}
                      size="small"
                    >
                      {agent.isEnabled ? (
                        <CheckCircle sx={{ color: 'green.500' }} />
                      ) : (
                        <Cancel sx={{ color: 'red.500' }} />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell align="center">
                    {agent.proceduralMemory?.steps?.length || 0}
                  </TableCell>
                  <TableCell>
                    {agent.proceduralMemory?.model || 'No model'}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5}>
                      <Tooltip title="View">
                        <IconButton
                          size="small"
                          onClick={() => handleView(agent)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={(e) => handleEdit(agent, e)}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Execute">
                        <IconButton
                          size="small"
                          onClick={(e) => handleExecute(agent, e)}
                          disabled={!agent.isEnabled}
                        >
                          <PlayArrow />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => handleDelete(agent, e)}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <AgentEditWizardModal
        open={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedAgent(null);
        }}
        agent={selectedAgent}
        onUpdate={handleAgentUpdate}
        allNodes={agents}
        getEdges={getEdges}
        setEdges={setEdges}
        workflowInputs={workflowInputs}
        setWorkflowInputs={setWorkflowInputs}
      />

      <AgentCreateWizardModal
        open={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setSelectedAgent(null);
        }}
        onUpdate={handleAgentUpdate}
      />

      <AgentViewModal
        open={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedAgent(null);
        }}
        agent={selectedAgent}
      />

      <ExecuteWorkflowModal
        open={showExecuteModal}
        onClose={() => {
          setShowExecuteModal(false);
          setSelectedAgent(null);
        }}
        workflow={{
          id: selectedAgent?.id || '',
          input: selectedAgent?.requiredParameters?.reduce((acc, param) => ({
            ...acc,
            [param]: ''
          }), {}) || {}
        }}
        useJsonEndpoint={false}
        isAgentExecution={true}
      />

      {selectedAgent && (
        <AgentStatusModal
          open={showStatusModal}
          onClose={() => {
            setShowStatusModal(false);
            setSelectedAgent(null);
          }}
          onConfirm={handleStatusConfirm}
          isEnabled={selectedAgent.isEnabled}
        />
      )}

      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
      >
        <DialogTitle>Delete Agent</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete agent "{selectedAgent?.id}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentsDashboardPage;